/**
 * Maps EmployeeDTO (full employee sync payload) to AttendanceEmployee (timelog lookup shape).
 * Used by LocalEmployeeRepository.findByCodeOrDui to return a locally-cached employee
 * in the same shape that RemoteEmployeeLookupRepository would produce.
 */
import { toTitleCase, buildPhotoUrl } from '@/shared/utils/mapping';
import type { EmployeeDTO } from '../dto/EmployeeDTO';
import type { AttendanceEmployee } from '@/modules/attendance/infrastructure/mappers/timelogMapper';

const AVATAR_COLORS = [
  '#2485F4', '#28C76F', '#EA5455', '#53DBD4',
  '#FF9F43', '#7367F0', '#00CFE8', '#FF6B6B',
] as const;

function colorForPerson(idPerson: number): string {
  return AVATAR_COLORS[idPerson % AVATAR_COLORS.length]!;
}

function initials(first: string, last: string): string {
  return `${first.charAt(0)}${last.charAt(0)}`.toUpperCase();
}

const TEMPLATE_PIN = 5;

export function toAttendanceEmployeeFromEmployeeDTO(dto: EmployeeDTO): AttendanceEmployee {
  const firstName = toTitleCase(dto.first_name);
  const lastName  = toTitleCase(dto.last_name1);

  const pinBiometric = dto.biometric_data?.find(
    (b) => b.id_template_biometric === TEMPLATE_PIN,
  ) ?? null;
  const pinCode = pinBiometric && Array.isArray(pinBiometric.biometric_data)
    ? (pinBiometric.biometric_data as number[]).join('')
    : null;

  return {
    idPerson:       dto.id_person,
    idEntity:       dto.id_entity,
    code:           dto.code,
    fullName:       `${firstName} ${lastName}`,
    initials:       initials(firstName, lastName),
    avatarColor:    colorForPerson(dto.id_person),
    dui:            dto.dui_person,
    department:     dto.entity_type,
    pinBiometricId: pinBiometric?.nro_biometric ?? null,
    pinCode,
    photoUrl:       buildPhotoUrl(dto.photo_rekognition ?? dto.photo),
  };
}
